import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';

/**
 * JSON fetch for API routes. Loads once on mount; call `mutate()` after writes.
 *
 * Polling and focus refetch are **off by default**. Pass `pollMs` or `refetchOnFocus`
 * only when live updates are required — prefer `mutate()` after mutations instead.
 */
export function useApiJson<T>(
  path: string | null,
  options?: {
    /** Opt-in interval polling in ms. Omit on new pages unless live updates are required. */
    pollMs?: number;
    /** When true, silently refetch on window focus. Off by default. */
    refetchOnFocus?: boolean;
    /** When false, skip fetching (e.g. gated on auth) */
    enabled?: boolean;
  },
) {
  const enabled = options?.enabled !== false;
  const pollMs = options?.pollMs;
  const refetchOnFocus = options?.refetchOnFocus === true;

  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(() => !!path && enabled);
  const [error, setError] = useState<Error | null>(null);

  const controller = useRef<AbortController | null>(null);

  const load = useCallback(async (background = false) => {
    if (!path || !enabled) {
      setLoading(false);
      return;
    }

    controller.current?.abort();
    const c = new AbortController();
    controller.current = c;

    if (!background) {
      setLoading(true);
    }
    setError(null);

    try {
      const json = await apiFetch<T>(path, { signal: c.signal });
      setData(json);
    } catch (e) {
      if ((e as Error).name === 'AbortError') return;
      setError(e as Error);
      if (!background) {
        setData(undefined);
      }
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  }, [path, enabled]);

  useEffect(() => {
    void load(false);
    return () => controller.current?.abort();
  }, [load]);

  useEffect(() => {
    if (!pollMs || !path || !enabled) return;
    const id = setInterval(() => void load(true), pollMs);
    return () => clearInterval(id);
  }, [pollMs, path, enabled, load]);

  useEffect(() => {
    if (!refetchOnFocus) return;
    const fn = () => void load(true);
    window.addEventListener('focus', fn);
    return () => window.removeEventListener('focus', fn);
  }, [load, refetchOnFocus]);

  const mutate = useCallback(async () => {
    await load(true);
  }, [load]);

  return { data, loading, error, mutate } as const;
}
